import { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HardHat, Copy, Sparkles, Trophy } from "lucide-react";

export const QuoteComparison = ({ quotes, onCopyLink }) => {
  if (!quotes.length) return null;
  const sym = quotes[0].currency_symbol || "$";
  const est = quotes.find((q) => q.our_estimate != null)?.our_estimate ?? null;
  const replied = quotes.filter((q) => q.status === "quoted" && q.price != null).sort((a, b) => a.price - b.price);
  const pending = quotes.filter((q) => q.status !== "quoted");
  const bestId = replied[0]?.quote_id;

  const deltaOf = (price) => (est ? Math.round(((price - est) / est) * 100) : null);

  return (
    <div className="mt-4 border border-border" data-testid="quotes-section">
      <div className="flex items-center gap-2 border-b border-border bg-secondary/20 px-4 py-3">
        <HardHat className="h-4 w-4 text-primary" />
        <span className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
          Quote comparison ({quotes.length})
        </span>
      </div>

      <div className="hidden grid-cols-12 gap-3 border-b border-border bg-secondary/10 px-4 py-2 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground sm:grid">
        <span className="col-span-4">Bidder</span>
        <span className="col-span-3 text-right">Price</span>
        <span className="col-span-2 text-right">vs. AI estimate</span>
        <span className="col-span-3">Note</span>
      </div>

      <div className="divide-y divide-border">
        {est != null && (
          <div className="grid grid-cols-12 items-center gap-3 bg-primary/5 px-4 py-3" data-testid="comparison-ai-row">
            <div className="col-span-12 flex items-center gap-2 sm:col-span-4">
              <Sparkles className="h-4 w-4 shrink-0 text-primary" />
              <div>
                <div className="font-heading text-sm font-semibold">Costobuild AI estimate</div>
                <div className="font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">Your reference scope</div>
              </div>
            </div>
            <div className="col-span-6 text-left font-mono2 text-lg font-bold text-primary sm:col-span-3 sm:text-right">{money(est, sym)}</div>
            <div className="col-span-3 hidden text-right font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground sm:col-span-2 sm:block">baseline</div>
            <div className="col-span-3 hidden sm:block" />
          </div>
        )}

        {replied.map((q) => {
          const d = deltaOf(q.price);
          const best = q.quote_id === bestId && replied.length > 1;
          return (
            <div key={q.quote_id} className={`grid grid-cols-12 items-center gap-3 px-4 py-3 ${best ? "bg-green-500/5" : ""}`} data-testid={`quote-row-${q.quote_id}`}>
              <div className="col-span-12 min-w-0 sm:col-span-4">
                <div className="flex items-center gap-2">
                  <span className="truncate font-heading text-sm font-semibold">{q.contractor_name || q.contractor_email}</span>
                  {best && (
                    <span className="flex items-center gap-1 border border-green-500/50 bg-green-500/10 px-1.5 py-0.5 font-mono2 text-[9px] uppercase tracking-widest text-green-400" data-testid={`best-bid-${q.quote_id}`}>
                      <Trophy className="h-3 w-3" /> Best bid
                    </span>
                  )}
                </div>
                <div className="truncate font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">{q.contractor_email}</div>
              </div>
              <div className="col-span-6 font-mono2 text-lg font-bold text-primary sm:col-span-3 sm:text-right" data-testid={`quote-price-${q.quote_id}`}>{money(q.price, sym)}</div>
              <div className="col-span-6 text-right sm:col-span-2">
                {d != null && (
                  <span className={`font-mono2 text-[11px] uppercase tracking-widest ${d > 0 ? "text-red-400" : "text-green-400"}`}>
                    {d > 0 ? "+" : ""}{d}%
                  </span>
                )}
              </div>
              <div className="col-span-12 truncate text-xs text-muted-foreground sm:col-span-3">{q.note || "—"}</div>
            </div>
          );
        })}

        {pending.map((q) => (
          <div key={q.quote_id} className="grid grid-cols-12 items-center gap-3 px-4 py-3 opacity-80" data-testid={`quote-row-${q.quote_id}`}>
            <div className="col-span-12 min-w-0 sm:col-span-4">
              <div className="truncate font-heading text-sm font-semibold">{q.contractor_name || q.contractor_email}</div>
              <div className="truncate font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">{q.contractor_email}</div>
            </div>
            <div className="col-span-6 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground sm:col-span-3 sm:text-right">Awaiting reply</div>
            <div className="col-span-6 flex justify-end sm:col-span-5">
              <Button variant="outline" size="sm" onClick={() => onCopyLink(q.reply_url)} data-testid={`copy-link-${q.quote_id}`}
                className="h-7 rounded-sm border-border font-mono2 text-[10px] uppercase tracking-widest">
                <Copy className="mr-1 h-3 w-3" /> Reply link
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
